import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { StatusBadge, LevelBadge } from "@/components/StatusBadge";
import { Search } from "lucide-react";

export default function Users() {
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState("freelancer");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    const params = {};
    if (role) params.role = role;
    if (status) params.status = status;
    if (q) params.q = q;
    const { data } = await api.get("/users", { params });
    setUsers(data || []);
  };
  useEffect(() => { load(); }, [role, status]);

  return (
    <AppLayout title="Users" subtitle="Directory">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
          <Input placeholder="Search by name or email" value={q} onChange={e=>setQ(e.target.value)} onKeyDown={(e)=>{if (e.key === "Enter") load();}} className="pl-9" autoCapitalize="none" autoCorrect="off" data-testid="input-search-users"/>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Select value={role || "all"} onValueChange={v=>setRole(v==="all"?"":v)}>
            <SelectTrigger className="flex-1 sm:flex-none sm:w-40 min-w-[140px]" data-testid="select-role-filter"><SelectValue placeholder="All roles"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="freelancer">Freelancer</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status || "all"} onValueChange={v=>setStatus(v==="all"?"":v)}>
            <SelectTrigger className="flex-1 sm:flex-none sm:w-44 min-w-[140px]" data-testid="select-status-users"><SelectValue placeholder="All statuses"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="btn-apply-filter">Apply</Button>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="data-table">
          <thead><tr>
            <th>Name</th><th>Role</th><th>Country</th><th>Status</th><th>Score</th><th>Skills</th>
          </tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="cursor-pointer" onClick={()=>nav(`/users/${u.id}`)} data-testid={`row-user-${u.id}`}>
                <td><b>{u.name}</b><div className="text-xs text-slate-500">{u.email}</div></td>
                <td className="uppercase text-xs tracking-wider text-slate-600">{u.role}</td>
                <td>{u.country || "—"}</td>
                <td><StatusBadge kind="user" value={u.status}/></td>
                <td>{u.tester_level ? <LevelBadge level={u.tester_level}/> : "—"}</td>
                <td className="text-xs">{(u.testing_skills || []).slice(0,3).join(", ")}</td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-500">No users found.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3" data-testid="users-mobile-list">
        {users.length === 0 && <div className="bg-white border border-slate-200 rounded-md p-6 text-center text-slate-500">No users found.</div>}
        {users.map(u => (
          <button key={u.id} onClick={()=>nav(`/users/${u.id}`)} className="w-full text-left bg-white border border-slate-200 rounded-md p-4 hover:border-slate-300 transition-colors" data-testid={`card-user-${u.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0 flex-1">
                <div className="font-display font-semibold text-slate-900 truncate">{u.name}</div>
                <div className="text-xs text-slate-500 truncate">{u.email}</div>
              </div>
              <StatusBadge kind="user" value={u.status}/>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
              <span className="uppercase tracking-wider">{u.role}</span>
              <span>•</span>
              <span>{u.country || "—"}</span>
              {u.tester_level && (<><span>•</span><LevelBadge level={u.tester_level}/></>)}
            </div>
            {(u.testing_skills || []).length > 0 && (
              <div className="text-xs text-slate-500 mt-2 truncate">{(u.testing_skills || []).slice(0, 3).join(", ")}</div>
            )}
          </button>
        ))}
      </div>
    </AppLayout>
  );
}
